/**
 * The property sidebar's Units table — every field on the backend `Unit`
 * model (eastview-os `app/models/pipeline.py` → `class Unit`) as a column,
 * edited in place. The same card-table look as the Units section on the
 * Properties-tab card (`CARD_GRID_SX`), so the two read as one table.
 *
 *   - A row's pinned Edit button turns that row's cells into inputs; the
 *     pinned Save button writes it (`POST update_unit`, changed fields only),
 *     Cancel drops the draft. One row edits at a time.
 *   - "Add unit" appends a blank row already in edit mode; Save on it
 *     creates the unit (`POST create_unit`, empty values pruned). Same row,
 *     same buttons — only the endpoint differs.
 *   - Enter saves, Escape cancels, while focus is in the row's inputs.
 *
 * The draft lives in React state and reaches the cells through context (AG
 * Grid renders cell components as portals, so context flows into them), so
 * typing never rebuilds `rowData` and inputs keep their focus. Select options
 * come from the `unit` model schema (`useModelFieldOptions`), with the enums
 * copied verbatim as the not-loaded-yet fallback.
 */
import {
  AddCircleOutline,
  CloseRounded,
  EditOutlined,
  SaveOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import PropTypes from "prop-types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import formatDollar from "../../../_src_shared/utils/functions/formatDollar";
import { useFetchedSelectionsRequestsHook } from "../../../hooks/useFetchedSelectionsRequestsHook";
import { useModelFieldOptions } from "../../../hooks/useModelFieldOptions";
import { useUnderwritingHook } from "../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../redux/actions/loanDrilldownActions";
import { loanPipelineClear } from "../../../redux/actions/loanPipelineActions";
import {
  ActionCell,
  actionColumn,
} from "../../../screens/admin/shared/adminTableActions";
import {
  CARD_GRID_COL_DEF,
  CARD_GRID_SX,
  CARD_TABLE_BOX_SX,
} from "../../../screens/LoanDrilldownScreen/shared/cardGrid";
import { level1, slate } from "../../../theme/colors";
import { formatBoolean } from "../../../utils/booleans/formatBoolean";
import AgGridTableWrapper from "../../agGrid/AgGridTableWrapper";
import DateInput from "../../inputs/DateInput";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";

// Verbatim from pipeline.py — fallbacks only until the `unit` schema loads.
const LEASE_TYPES_FALLBACK = [
  "12-Month Lease - Occupied",
  "24-Month Lease",
  "Greater than 3-year lease",
  "Month-to-Month",
  "12-Month Lease - Signed with Deposit",
  "Vacant",
  "Short-Term Rental",
  "TBD",
];
const COMMERCIAL_USES_FALLBACK = [
  "Office",
  "Restaurant",
  "Other Food Service",
  "Automotive Service",
  "Dry Cleaner",
  "Industrial - Factory or Other",
  "Other Retail",
  "TBD",
];

const NEW_KEY = "__new";
const ROW_H = 40;

/**
 * One entry per `Unit` field, in column order. `type` picks both the read
 * display and the in-row editor.
 */
const UNIT_FIELDS = [
  { field: "unitNumber", header: "Unit", type: "text", width: 64, pinned: "left" },
  { field: "leaseType", header: "Lease", type: "select", options: "leaseType", width: 210 },
  { field: "leaseStartDate", header: "Lease start", type: "date", width: 170 },
  { field: "leaseEndDate", header: "Lease end", type: "date", width: 170 },
  { field: "unitSquareFootage", header: "Sq ft", type: "float", width: 100 },
  { field: "unitMonthlyInPlaceRent", header: "In-place rent", type: "dollar", width: 130 },
  { field: "unitMonthlyMarketRent", header: "Market rent", type: "dollar", width: 130 },
  { field: "airbnbIncome", header: "STR income", type: "dollar", width: 130 },
  { field: "unitVacant", header: "Vacant", type: "bool", width: 90 },
  { field: "residentialUnit", header: "Residential", type: "bool", width: 110 },
  { field: "commercialUnit", header: "Commercial", type: "bool", width: 116 },
  { field: "commercialUse", header: "Commercial use", type: "select", options: "commercialUse", width: 190 },
  { field: "unitNotes", header: "Notes", type: "text", width: 240 },
];

const isNumeric = (t) => t === "dollar" || t === "float";

/* ── draft ⇄ unit ─────────────────────────────────────── */

// Inputs hold strings for numbers so a half-typed "1,2" or "" survives.
const toDraft = (unit) =>
  Object.fromEntries(
    UNIT_FIELDS.map(({ field, type }) => {
      const v = unit?.[field];
      if (type === "bool") return [field, v ?? null];
      if (isNumeric(type)) return [field, v == null ? "" : String(v)];
      return [field, v ?? ""];
    }),
  );

const fromDraft = (draft) =>
  Object.fromEntries(
    UNIT_FIELDS.map(({ field, type }) => {
      const v = draft?.[field];
      if (type === "bool") return [field, v ?? null];
      if (isNumeric(type)) {
        const s = String(v ?? "").replace(/[$,\s]/g, "");
        if (s === "") return [field, null];
        const n = Number(s);
        return [field, Number.isFinite(n) ? n : null];
      }
      return [field, v === "" || v == null ? null : v];
    }),
  );

/* ── read display ─────────────────────────────────────── */

const money = (v) => {
  const n = Number(v);
  return v != null && v !== "" && Number.isFinite(n) && n !== 0
    ? formatDollar(n)
    : "—";
};

const displayValue = (type, v) => {
  switch (type) {
    case "dollar":
      return money(v);
    case "float":
      return v != null && v !== "" && Number(v) ? Number(v).toLocaleString() : "—";
    case "date":
      return formatDate(v, "—", "M/d/yyyy");
    case "bool":
      return formatBoolean(v, { fallback: "—" });
    default:
      return v == null || v === "" ? "—" : String(v);
  }
};

/* ── edit context + cells ─────────────────────────────── */

const UnitEditContext = createContext(null);

const INPUT_SX = {
  "& .MuiInputBase-root": { height: 30, fontSize: 12.5, background: "#fff" },
  "& .MuiInputBase-input": { py: 0, px: "8px" },
  "& .MuiInputAdornment-root": { mr: "-4px" },
  "& .MuiInputAdornment-root p": { fontSize: 12.5 },
};

const FieldEditor = ({ spec }) => {
  const { draft, setField, options, onKeyDown } = useContext(UnitEditContext);
  const value = draft?.[spec.field];

  if (spec.type === "bool") {
    return (
      <Flx ac jc sx={{ height: "100%", width: "100%" }}>
        <Checkbox
          size="small"
          checked={value === true}
          onChange={(e) => setField(spec.field, e.target.checked)}
          inputProps={{ "aria-label": spec.header }}
        />
      </Flx>
    );
  }

  if (spec.type === "date") {
    return (
      <Flx ac sx={{ height: "100%", width: "100%" }}>
        <DateInput
          value={value || null}
          onChange={(iso) => setField(spec.field, iso || "")}
          size="small"
          sx={INPUT_SX}
        />
      </Flx>
    );
  }

  if (spec.type === "select") {
    const opts = options[spec.options] || [];
    // Commercial use only applies to a commercial unit (the model's rule).
    const disabled = spec.field === "commercialUse" && draft?.commercialUnit !== true;
    return (
      <Flx ac sx={{ height: "100%", width: "100%" }}>
        <TextField
          select
          fullWidth
          size="small"
          value={value ?? ""}
          disabled={disabled}
          onChange={(e) => setField(spec.field, e.target.value)}
          sx={INPUT_SX}
          inputProps={{ "aria-label": spec.header }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {/* A stored value the enum no longer has stays selectable, not blank. */}
          {value && !opts.includes(value) ? (
            <MenuItem value={value}>{value}</MenuItem>
          ) : null}
          {opts.map((o) => (
            <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
              {o}
            </MenuItem>
          ))}
        </TextField>
      </Flx>
    );
  }

  return (
    <Flx ac sx={{ height: "100%", width: "100%" }}>
      <TextField
        fullWidth
        size="small"
        value={value ?? ""}
        onChange={(e) => setField(spec.field, e.target.value)}
        onKeyDown={onKeyDown}
        sx={INPUT_SX}
        inputProps={{
          "aria-label": spec.header,
          inputMode: isNumeric(spec.type) ? "decimal" : undefined,
          style: isNumeric(spec.type) ? { textAlign: "right" } : undefined,
        }}
        InputProps={
          spec.type === "dollar"
            ? { startAdornment: <InputAdornment position="start">$</InputAdornment> }
            : undefined
        }
      />
    </Flx>
  );
};

FieldEditor.propTypes = { spec: PropTypes.object.isRequired };

const UnitFieldCell = (p) => {
  const ctx = useContext(UnitEditContext);
  const { spec } = p;
  if (ctx?.editKey === p.data?.__rowKey) return <FieldEditor spec={spec} />;
  const text = displayValue(spec.type, p.data?.[spec.field]);
  if (spec.type === "bool") {
    return (
      <Flx ac jc sx={{ height: "100%" }}>
        {text}
      </Flx>
    );
  }
  return text;
};

const UnitActionsCell = (p) => {
  const { editKey, saving, startEdit, cancel, save } = useContext(UnitEditContext);
  const rowKey = p.data?.__rowKey;

  if (editKey === rowKey) {
    return (
      <ActionCell>
        <Tooltip title={rowKey === NEW_KEY ? "Create unit" : "Save unit"}>
          <span>
            <IconButton
              size="small"
              color="primary"
              disabled={saving}
              onClick={save}
              sx={{ width: "100%", height: "100%", borderRadius: 0 }}
            >
              {saving ? <CircularProgress size={14} /> : <SaveOutlined fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Cancel">
          <span>
            <IconButton
              size="small"
              disabled={saving}
              onClick={cancel}
              sx={{ width: "100%", height: "100%", borderRadius: 0 }}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </ActionCell>
    );
  }

  return (
    <ActionCell>
      <Tooltip title={editKey ? "Finish the row being edited first" : "Edit unit"}>
        <span>
          <IconButton
            size="small"
            disabled={!!editKey}
            onClick={() => startEdit(p.data)}
            sx={{ width: "100%", height: "100%", borderRadius: 0 }}
          >
            <EditOutlined fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </ActionCell>
  );
};

// Keys typed into a row's inputs belong to the input, not to AG Grid's
// navigation (arrows, Tab, space, typing-to-edit).
const suppressKeysInInputs = (p) =>
  ["INPUT", "TEXTAREA"].includes(p.event?.target?.tagName);

// Header-click sorting. Unit numbers are "1", "2", "10", "B" — sort them
// naturally; blanks sort last either way.
const naturalCompare = (a, b) => {
  const blankA = a == null || a === "";
  const blankB = b == null || b === "";
  if (blankA || blankB) return blankA === blankB ? 0 : blankA ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" || typeof b === "boolean") return Number(!!a) - Number(!!b);
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};

// The unsaved new row stays at the bottom whatever the sort.
const keepNewRowLast = ({ nodes }) => {
  const i = nodes.findIndex((n) => n.data?.__rowKey === NEW_KEY);
  if (i > -1) nodes.push(nodes.splice(i, 1)[0]);
};

const COLUMNS = [
  ...UNIT_FIELDS.map((spec) => ({
    headerName: spec.header,
    colId: spec.field,
    field: spec.field,
    width: spec.width,
    pinned: spec.pinned,
    sortable: true,
    comparator: naturalCompare,
    cellRenderer: UnitFieldCell,
    cellRendererParams: { spec },
    cellClass: isNumeric(spec.type) ? "num" : undefined,
    headerClass: isNumeric(spec.type) ? "ag-right-aligned-header" : undefined,
    suppressKeyboardEvent: suppressKeysInInputs,
  })),
  actionColumn({ colId: "actions", count: 2, cellRenderer: UnitActionsCell }),
];

const GRID_SX = {
  ...CARD_GRID_SX,
  "--ag-cell-horizontal-padding": "10px",
  // A row in edit mode carries the level-1 tint so "which row am I editing"
  // reads at a glance.
  "& .unit-row-editing": { background: level1.hoverTint },
};

/* ── the table ────────────────────────────────────────── */

const PropertyUnitsGrid = ({
  units,
  loanId,
  propertyId,
  createRequested,
  onCreateHandled,
}) => {
  const dispatch = useDispatch();
  const { createSubjectPropertyUnit, updateSubjectPropertyUnit } =
    useUnderwritingHook();
  const { getModelSelectOptions } = useFetchedSelectionsRequestsHook();

  const unitSchema = useSelector(
    (state) => state.fetchedSelections?.["modelSchema:unit"],
  );
  useEffect(() => {
    if (!unitSchema) getModelSelectOptions({ objectType: "unit" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitSchema]);
  const leaseTypes = useModelFieldOptions("leaseType", LEASE_TYPES_FALLBACK, {
    objectType: "unit",
  });
  const commercialUses = useModelFieldOptions(
    "commercialUse",
    COMMERCIAL_USES_FALLBACK,
    { objectType: "unit" },
  );
  const options = useMemo(
    () => ({ leaseType: leaseTypes, commercialUse: commercialUses }),
    [leaseTypes, commercialUses],
  );

  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  // A different property never inherits a half-edited row.
  useEffect(() => {
    setEditKey(null);
    setDraft(null);
  }, [propertyId]);

  const startAdd = useCallback(() => {
    setEditKey(NEW_KEY);
    setDraft(toDraft({}));
  }, []);

  // "Add unit" from outside the tab (the card, the Summary quick action).
  // Declared after the reset above so a switch-and-add lands as an add.
  useEffect(() => {
    if (!createRequested) return;
    startAdd();
    onCreateHandled?.();
  }, [createRequested, startAdd, onCreateHandled]);

  const rows = useMemo(() => {
    const list = (units || []).map((u, i) => ({
      ...u,
      __rowKey: String(u?._id || `idx-${i}`),
    }));
    if (editKey === NEW_KEY) list.push({ __rowKey: NEW_KEY });
    return list;
  }, [units, editKey]);

  const startEdit = useCallback((row) => {
    setEditKey(row.__rowKey);
    setDraft(toDraft(row));
  }, []);

  const cancel = useCallback(() => {
    setEditKey(null);
    setDraft(null);
  }, []);

  const setField = useCallback(
    (field, value) => setDraft((d) => ({ ...d, [field]: value })),
    [],
  );

  const onSaved = useCallback(
    (response) => {
      dispatch(loanDrilldownSet(response));
      dispatch(loanPipelineClear(response));
      setEditKey(null);
      setDraft(null);
    },
    [dispatch],
  );

  const save = useCallback(() => {
    if (!editKey || !draft || saving) return;
    const next = fromDraft(draft);

    if (editKey === NEW_KEY) {
      // Create: send only what was entered.
      const body = Object.fromEntries(
        Object.entries(next).filter(([, v]) => v !== null),
      );
      setSaving(true);
      createSubjectPropertyUnit({
        loanId,
        propertyId,
        propertyData: body,
        onSuccessMsg: "Unit added",
        onSuccessFn: onSaved,
        onCompleteFn: () => setSaving(false),
      });
      return;
    }

    // Update: only the fields that moved (compared in the same normalised form).
    const row = rows.find((r) => r.__rowKey === editKey);
    const before = fromDraft(toDraft(row));
    const changed = Object.fromEntries(
      Object.entries(next).filter(([k, v]) => v !== before[k]),
    );
    if (!Object.keys(changed).length || !row?._id) {
      cancel();
      return;
    }
    setSaving(true);
    updateSubjectPropertyUnit({
      loanId,
      propertyId,
      unitId: row._id,
      unitData: changed,
      onSuccessMsg: "Unit saved",
      onSuccessFn: onSaved,
      onCompleteFn: () => setSaving(false),
    });
  }, [
    editKey,
    draft,
    saving,
    rows,
    loanId,
    propertyId,
    createSubjectPropertyUnit,
    updateSubjectPropertyUnit,
    onSaved,
    cancel,
  ]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        save();
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancel();
      }
    },
    [save, cancel],
  );

  const ctx = useMemo(
    () => ({ editKey, draft, setField, options, saving, startEdit, cancel, save, onKeyDown }),
    [editKey, draft, setField, options, saving, startEdit, cancel, save, onKeyDown],
  );

  const rowClassRules = useMemo(
    () => ({ "unit-row-editing": (p) => p.data?.__rowKey === editKey }),
    [editKey],
  );

  return (
    <UnitEditContext.Provider value={ctx}>
      <Flx column g={1.5}>
        {/* Section header: title on the left, Add Unit at the top right. */}
        <Flx ac jb g={2}>
          <Flx column g={0.5} sx={{ minWidth: 0 }}>
            <Txt
              sx={{
                fontSize: 14,
                fontWeight: 500,
                color: slate[900],
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Property Units ({(units || []).length})
            </Txt>
            <Txt secondary>
              {(units || []).length === 0
                ? "No units added yet."
                : "Click a row's edit button to change it, then save."}
            </Txt>
          </Flx>
          <Button
            size="small"
            disabled={!!editKey}
            startIcon={<AddCircleOutline sx={{ fontSize: "16px !important" }} />}
            onClick={startAdd}
            sx={{ flexShrink: 0 }}
          >
            Add Unit
          </Button>
        </Flx>
        {rows.length ? (
          <Box sx={{ ...CARD_TABLE_BOX_SX, background: "#fff" }}>
            <AgGridTableWrapper
              compactTable
              noBorder
              suppressMinHeightWhenPopulated={rows}
              sx={GRID_SX}
            >
              <AgGridReact
                rowData={rows}
                columnDefs={COLUMNS}
                defaultColDef={CARD_GRID_COL_DEF}
                getRowId={(p) => p.data.__rowKey}
                rowClassRules={rowClassRules}
                postSortRows={keepNewRowLast}
                rowHeight={ROW_H}
                domLayout="autoHeight"
                suppressCellFocus
                suppressColumnVirtualisation
              />
            </AgGridTableWrapper>
          </Box>
        ) : null}
      </Flx>
    </UnitEditContext.Provider>
  );
};

PropertyUnitsGrid.propTypes = {
  units: PropTypes.array,
  loanId: PropTypes.string,
  propertyId: PropTypes.string,
  createRequested: PropTypes.bool,
  onCreateHandled: PropTypes.func,
};

export default PropertyUnitsGrid;
